import { useEffect, useId, useState } from 'react'
import { TreeIcon } from '../../tree/treeIcons'

/**
 * The few controls the item editor is built from: a labelled field with its
 * message underneath, a set of option cards, a section, and a stepper.
 */

/** A label, the control, then either the error or a quiet note — never both. */
export function Field({
  label,
  note,
  error,
  wide = false,
  children,
}: {
  label: string
  note?: React.ReactNode
  error?: string | null | undefined
  wide?: boolean
  children: React.ReactNode
}): JSX.Element {
  return (
    <label className={`ie-field${wide ? ' ie-field--wide' : ''}${error ? ' ie-field--error' : ''}`}>
      <span className="ie-field__label">{label}</span>
      {children}
      {error ? (
        <span className="ie-field__error" role="alert">
          {error}
        </span>
      ) : note ? (
        <span className="ie-field__note">{note}</span>
      ) : null}
    </label>
  )
}

export interface Option<T extends string> {
  readonly value: T
  readonly title: string
  readonly note: string
  readonly icon: React.ReactNode
}

/**
 * A radio group drawn as cards: a picture of the answer, the answer, and one
 * line saying what it means. The whole card is the target.
 */
export function OptionCards<T extends string>({
  legend,
  value,
  options,
  onChange,
  disabled = false,
  footnote,
}: {
  legend: string
  value: T
  options: readonly Option<T>[]
  onChange: (next: T) => void
  disabled?: boolean
  footnote?: React.ReactNode
}): JSX.Element {
  const name = useId()
  return (
    <fieldset className="ie-options" disabled={disabled}>
      <legend className="ie-field__label">{legend}</legend>
      <div className={`ie-options__grid ie-options__grid--${options.length}`}>
        {options.map((o) => (
          <label key={o.value} className={`ie-option${value === o.value ? ' ie-option--on' : ''}`}>
            <input type="radio" name={name} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span className="ie-option__icon" aria-hidden="true">
              {o.icon}
            </span>
            <span className="ie-option__text">
              <span className="ie-option__title">{o.title}</span>
              <span className="ie-option__note">{o.note}</span>
            </span>
          </label>
        ))}
      </div>
      {footnote ? <p className="ie-field__note ie-options__foot">{footnote}</p> : null}
    </fieldset>
  )
}

/** A titled block of the form. */
export function EditorSection({
  title,
  intro,
  children,
}: {
  title: string
  intro?: React.ReactNode
  children: React.ReactNode
}): JSX.Element {
  return (
    <section className="ie-section">
      <header className="ie-section__head">
        <h3 className="ie-section__title">{title}</h3>
        {intro ? <p className="ie-section__intro">{intro}</p> : null}
      </header>
      <div className="ie-section__body">{children}</div>
    </section>
  )
}

/**
 * − n +, with the number typeable. Whole numbers for slots; a counted part may
 * be 0.5 m, so the field takes a decimal there. `min` is the least the field
 * accepts — the buttons never step below it.
 */
export function Stepper({
  value,
  onChange,
  min,
  label,
  decimal = false,
}: {
  value: number
  onChange: (next: number) => void
  min: number
  label: string
  decimal?: boolean
}): JSX.Element {
  // The text is its own state so the field can be emptied and retyped; the
  // number only moves when what is typed is one.
  const [text, setText] = useState(String(value))
  useEffect(() => setText(String(value)), [value])
  return (
    <span className="ie-stepper" role="group" aria-label={label}>
      <button type="button" aria-label="One fewer" disabled={value - 1 < min} onClick={() => onChange(value - 1)}>
        <TreeIcon name="minus" />
      </button>
      <input
        inputMode={decimal ? 'decimal' : 'numeric'}
        value={text}
        aria-label={label}
        onChange={(e) => {
          setText(e.target.value)
          const n = Number(e.target.value)
          if (e.target.value.trim() !== '' && Number.isFinite(n) && n >= min) onChange(decimal ? n : Math.floor(n))
        }}
        onBlur={() => setText(String(value))}
      />
      <button type="button" aria-label="One more" onClick={() => onChange(value + 1)}>
        <TreeIcon name="plus" />
      </button>
    </span>
  )
}
